// components/viz/Plot3D.jsx
// 3D SVG 可视化（散点/相面）· 自实现旋转矩阵 + 深度排序

import { useState, useEffect, useCallback, useRef } from 'react';
import { PHASE_COLORS } from '../../data/systems';

/* ════════════════════════════════════════════════════════════════════
   3D · Interactive SVG canvas (manual rotation matrix + depth sort)
   No external 3D library — pure React + SVG.
   ════════════════════════════════════════════════════════════════════ */
export default function Plot3D({ mode, system, allData, phaseList, autoRotate, onSelectPoint, selectedPointId }) {
  const [theta, setTheta] = useState(0.65);
  const [phi, setPhi]     = useState(0.42);
  const [scale, setScale] = useState(360);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);  // { sx, sy, data }
  const dragRef = useRef(null);
  const containerRef = useRef(null);
  const [, force] = useState(0);

  const W = 720, H = 540;
  const CX = W / 2, CY = H / 2 - 10;

  // Auto-rotate (pauses while user drags)
  useEffect(() => {
    if (!autoRotate) return;
    let raf;
    const tick = () => {
      if (!dragRef.current) setTheta(t => t + 0.0035);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoRotate]);

  // Wheel zoom (non-passive listener so we can preventDefault)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setScale(s => Math.max(150, Math.min(720, s * (1 - e.deltaY * 0.0014))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Mouse handlers
  const onMouseDown = (e) => { dragRef.current = { x: e.clientX, y: e.clientY }; force(n => n + 1); };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setTheta(t => t + dx * 0.008);
    setPhi(p => Math.max(-1.45, Math.min(1.45, p - dy * 0.008)));
    dragRef.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseUp = () => { dragRef.current = null; force(n => n + 1); };

  // 3D → 2D projection (rotate Y by theta, then X by phi, orthographic)
  const cosT = Math.cos(theta), sinT = Math.sin(theta);
  const cosP = Math.cos(phi),   sinP = Math.sin(phi);
  const project = useCallback((x, y, z) => {
    const x1 = x * cosT - z * sinT;
    const z1 = x * sinT + z * cosT;
    const y2 = y * cosP - z1 * sinP;
    const z2 = y * sinP + z1 * cosP;
    return { sx: CX + x1 * scale, sy: CY - y2 * scale, depth: z2 };
  }, [cosT, sinT, cosP, sinP, scale]);

  // Bounding box corners (unit cube centered on origin)
  const corners = [
    [-0.5, -0.5, -0.5],[ 0.5, -0.5, -0.5],[ 0.5, -0.5,  0.5],[-0.5, -0.5,  0.5],
    [-0.5,  0.5, -0.5],[ 0.5,  0.5, -0.5],[ 0.5,  0.5,  0.5],[-0.5,  0.5,  0.5],
  ];
  const edges = [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
  const projCorners = corners.map(c => project(c[0], c[1], c[2]));

  // Floor grid (10x10 lines on bottom face)
  const gridLines = [];
  for (let i = 0; i <= 10; i++) {
    const t = -0.5 + i / 10;
    gridLines.push([project(t, -0.5, -0.5), project(t, -0.5, 0.5)]);
    gridLines.push([project(-0.5, -0.5, t), project(0.5, -0.5, t)]);
  }

  /* ───────── SCATTER MODE ───────── */
  let scatterPoints = [];
  if (mode === "scatter") {
    scatterPoints = allData.map(pt => {
      const x = pt.w1 / 0.5 - 0.5;
      const z = -(pt.w2 / 0.5 - 0.5);
      const y = (pt.temp - system.tempRange[0]) / (system.tempRange[1] - system.tempRange[0]) - 0.5;
      const pIdx = phaseList.indexOf(pt.solidPhase);
      const color = PHASE_COLORS[pIdx % PHASE_COLORS.length] || "#64748b";
      const proj = project(x, y, z);
      return { ...pt, ...proj, color };
    }).sort((a, b) => a.depth - b.depth);
  }

  /* ───────── SURFACE MODE ───────── */
  let surfaceQuads = [];
  let surfacePoints = [];
  if (mode === "surface") {
    const cols = 22, rows = 22;
    const grid = [];
    for (let r = 0; r < rows; r++) {
      const row = [];
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1);
        const v = r / (rows - 1);
        const w1 = u * 0.5;
        const T  = system.tempRange[0] + v * (system.tempRange[1] - system.tempRange[0]);
        const w2sat = 0.05 + 0.18 * Math.exp(-Math.pow((w1 - 0.18) / 0.13, 2)) + (T - 25) * 0.0014;
        const x = u - 0.5;
        const y = w2sat / 0.5 - 0.5;            // map w2 [0..0.5] → y [-0.5..0.5]
        const z = -(v - 0.5);
        const proj = project(x, y, z);
        row.push({ ...proj, w2sat, T, w1, normY: y });
      }
      grid.push(row);
    }
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const v0 = grid[r][c], v1 = grid[r][c+1], v2 = grid[r+1][c+1], v3 = grid[r+1][c];
        const avgD = (v0.depth + v1.depth + v2.depth + v3.depth) / 4;
        const avgY = (v0.normY + v1.normY + v2.normY + v3.normY) / 4;
        const ratio = Math.min(1, Math.max(0, (avgY + 0.4) / 0.8));
        const hue = 220 - ratio * 200;          // blue → red gradient
        const lightness = 50 + ratio * 8;
        const avgW1 = (v0.w1 + v1.w1 + v2.w1 + v3.w1) / 4;
        const avgT  = (v0.T  + v1.T  + v2.T  + v3.T)  / 4;
        const avgW2 = (v0.w2sat + v1.w2sat + v2.w2sat + v3.w2sat) / 4;
        surfaceQuads.push({
          d: `M${v0.sx},${v0.sy} L${v1.sx},${v1.sy} L${v2.sx},${v2.sy} L${v3.sx},${v3.sy} Z`,
          fill: `hsl(${hue}, 72%, ${lightness}%)`,
          depth: avgD,
          w1: avgW1, T: avgT, w2sat: avgW2,
        });
      }
    }
    surfaceQuads.sort((a, b) => a.depth - b.depth);

    // Overlay actual experimental data points on surface
    surfacePoints = allData.map(pt => {
      const x = pt.w1 / 0.5 - 0.5;
      const z = -((pt.temp - system.tempRange[0]) / (system.tempRange[1] - system.tempRange[0]) - 0.5);
      const y = pt.w2 / 0.5 - 0.5;
      const pIdx = phaseList.indexOf(pt.solidPhase);
      const color = PHASE_COLORS[pIdx % PHASE_COLORS.length] || "#64748b";
      const proj = project(x, y, z);
      return { ...pt, ...proj, color };
    }).sort((a, b) => a.depth - b.depth);
  }

  // Origin (where axis arrows emanate from) — back-bottom-left corner
  const O = project(-0.5, -0.5, 0.5);

  // Axis arrow tips (with length ~0.4 units beyond)
  const xTip = project(-0.05, -0.5, 0.5);
  const yTip = project(-0.5, -0.05, 0.5);
  const zTip = project(-0.5, -0.5, -0.05);

  // Axis tick marks (3 ticks per axis)
  const axisTicks = [];
  for (let i = 1; i <= 3; i++) {
    const f = -0.5 + i * 0.25;  // -0.25, 0, 0.25
    axisTicks.push({
      kind: "x", pos: project(f, -0.5, 0.5),
      label: ((f + 0.5) * 0.5 * 100).toFixed(0),  // % of w1
    });
    axisTicks.push({
      kind: "y", pos: project(-0.5, f, 0.5),
      label: mode === "scatter"
        ? Math.round(system.tempRange[0] + (f + 0.5) * (system.tempRange[1] - system.tempRange[0]))
        : ((f + 0.5) * 0.5 * 100).toFixed(0),
    });
    axisTicks.push({
      kind: "z", pos: project(-0.5, -0.5, f),
      label: mode === "scatter"
        ? ((-(f - 0.5)) * 0.5 * 100).toFixed(0)
        : Math.round(system.tempRange[1] - (f + 0.5) * (system.tempRange[1] - system.tempRange[0])),
    });
  }

  const axisLabels = mode === "surface"
    ? { x: `w(${system.components[0]}) %`, y: `w(${system.components[1]}) %`, z: "T (°C)" }
    : { x: `w(${system.components[0]}) %`, y: "T (°C)", z: `w(${system.components[1]}) %` };

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      style={{
        width: "100%", height: "100%",
        background: "linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)",
        borderRadius: 12,
        position: "relative",
        overflow: "hidden",
        cursor: dragRef.current ? "grabbing" : "grab",
        userSelect: "none",
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "100%", display: "block" }}>
        <defs>
          <pattern id="grid3d" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#cbd5e1" strokeWidth="0.4" opacity="0.4" />
          </pattern>
          <radialGradient id="floorGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"  stopColor="#dbeafe" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#dbeafe" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width={W} height={H} fill="url(#grid3d)" opacity="0.5" />

        {/* Floor face (filled subtle) */}
        <polygon
          points={`${projCorners[0].sx},${projCorners[0].sy} ${projCorners[1].sx},${projCorners[1].sy} ${projCorners[2].sx},${projCorners[2].sy} ${projCorners[3].sx},${projCorners[3].sy}`}
          fill="#dbeafe" opacity="0.3"
        />

        {/* Floor grid */}
        {gridLines.map((ln, i) => (
          <line key={`gl-${i}`}
            x1={ln[0].sx} y1={ln[0].sy} x2={ln[1].sx} y2={ln[1].sy}
            stroke="#94a3b8" strokeWidth="0.4" opacity="0.35"
          />
        ))}

        {/* Bounding box edges (back ones first) */}
        {edges.map(([a, b], i) => {
          const avgZ = (projCorners[a].depth + projCorners[b].depth) / 2;
          const isBack = avgZ < 0;
          return (
            <line key={`e-${i}`}
              x1={projCorners[a].sx} y1={projCorners[a].sy}
              x2={projCorners[b].sx} y2={projCorners[b].sy}
              stroke={isBack ? "#cbd5e1" : "#64748b"}
              strokeWidth={isBack ? 0.6 : 1}
              strokeDasharray={isBack ? "3,3" : "none"}
              opacity={isBack ? 0.5 : 0.85}
            />
          );
        })}

        {/* Surface quads (depth-sorted, hoverable) */}
        {mode === "surface" && surfaceQuads.map((q, i) => (
          <path key={`q-${i}`} d={q.d} fill={q.fill} stroke="#0f172a" strokeWidth="0.4" opacity="0.82"
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => {
              const svgEl = e.currentTarget.ownerSVGElement;
              const pt = svgEl.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
              const m = svgEl.getScreenCTM().inverse();
              const local = pt.matrixTransform(m);
              setHoverInfo({ sx: local.x, sy: local.y, kind: "surface", w1: q.w1, T: q.T, w2sat: q.w2sat });
            }}
            onMouseLeave={() => setHoverInfo(null)}
            onClick={() => {
              if (onSelectPoint) onSelectPoint({
                kind: "surface_pred",
                w1: q.w1, w2: q.w2sat, ww: 1 - q.w1 - q.w2sat,
                temp: Math.round(q.T),
                solidPhase: "PC-CVAE 预测面",
                source: "PC-CVAE 模型",
              });
            }}
          />
        ))}

        {/* Scatter points (depth-sorted, LARGER, clickable) */}
        {mode === "scatter" && scatterPoints.map((p, i) => {
          const r = 5.5 + (p.depth + 0.5) * 3.2;  // bigger
          const op = 0.6 + (p.depth + 0.5) * 0.4;
          const isHovered = hoverIdx === p.id;
          const isSelected = selectedPointId === p.id;
          return (
            <g key={`p-${i}`}>
              {isSelected && (
                <circle cx={p.sx} cy={p.sy} r={Math.max(3, r) + 7} fill="none" stroke={p.color} strokeWidth="2" strokeDasharray="3,2" opacity="0.7" />
              )}
              <circle
                cx={p.sx} cy={p.sy} r={isHovered ? Math.max(4, r) + 3 : Math.max(3, r)}
                fill={p.color} opacity={op}
                stroke="#ffffff" strokeWidth="1.2"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => { setHoverIdx(p.id); setHoverInfo({ sx: p.sx, sy: p.sy, kind: "scatter", data: p }); }}
                onMouseLeave={() => { setHoverIdx(null); setHoverInfo(null); }}
                onClick={() => onSelectPoint && onSelectPoint({ kind: "experimental", ...p })}
              />
            </g>
          );
        })}

        {/* Surface overlay points (LARGER, clickable) */}
        {mode === "surface" && surfacePoints.map((p, i) => {
          const isHovered = hoverIdx === p.id;
          const isSelected = selectedPointId === p.id;
          return (
            <g key={`sp-${i}`}>
              {isSelected && (
                <circle cx={p.sx} cy={p.sy} r="11" fill="none" stroke={p.color} strokeWidth="2" strokeDasharray="3,2" opacity="0.8" />
              )}
              <circle
                cx={p.sx} cy={p.sy} r={isHovered ? 7 : 5}
                fill={p.color} opacity="0.98"
                stroke="#ffffff" strokeWidth="1.4"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => { setHoverIdx(p.id); setHoverInfo({ sx: p.sx, sy: p.sy, kind: "scatter", data: p }); }}
                onMouseLeave={() => { setHoverIdx(null); setHoverInfo(null); }}
                onClick={() => onSelectPoint && onSelectPoint({ kind: "experimental", ...p })}
              />
            </g>
          );
        })}

        {/* Axis arrows from origin (THICKER) */}
        <g>
          <line x1={O.sx} y1={O.sy} x2={xTip.sx} y2={xTip.sy} stroke="#ef4444" strokeWidth="2.8" />
          <circle cx={xTip.sx} cy={xTip.sy} r="4" fill="#ef4444" />
          <line x1={O.sx} y1={O.sy} x2={yTip.sx} y2={yTip.sy} stroke="#10b981" strokeWidth="2.8" />
          <circle cx={yTip.sx} cy={yTip.sy} r="4" fill="#10b981" />
          <line x1={O.sx} y1={O.sy} x2={zTip.sx} y2={zTip.sy} stroke="#0ea5e9" strokeWidth="2.8" />
          <circle cx={zTip.sx} cy={zTip.sy} r="4" fill="#0ea5e9" />
        </g>

        {/* Axis tick labels (LARGER) */}
        {axisTicks.map((tk, i) => (
          <text key={`ax-${i}`}
            x={tk.pos.sx + (tk.kind === "x" ? 0 : tk.kind === "z" ? -12 : -12)}
            y={tk.pos.sy + (tk.kind === "x" ? 18 : 5)}
            fontSize="12"
            fontWeight="600"
            fontFamily="'JetBrains Mono', monospace"
            fill="#475569"
            textAnchor={tk.kind === "x" ? "middle" : "end"}
          >{tk.label}</text>
        ))}

        {/* Axis labels (LARGER) */}
        <text x={xTip.sx + 16} y={xTip.sy + 7} fontSize="15" fontWeight="800" fill="#ef4444" fontFamily="'IBM Plex Sans', sans-serif">{axisLabels.x}</text>
        <text x={yTip.sx + 16} y={yTip.sy + 7} fontSize="15" fontWeight="800" fill="#10b981" fontFamily="'IBM Plex Sans', sans-serif">{axisLabels.y}</text>
        <text x={zTip.sx + 16} y={zTip.sy + 7} fontSize="15" fontWeight="800" fill="#0ea5e9" fontFamily="'IBM Plex Sans', sans-serif">{axisLabels.z}</text>

        {/* Floating tooltip when hovering points/quads */}
        {hoverInfo && hoverInfo.kind === "scatter" && (() => {
          const d = hoverInfo.data;
          // tooltip positioning - keep on screen
          const tipX = Math.min(W - 230, hoverInfo.sx + 14);
          const tipY = Math.max(10, hoverInfo.sy - 90);
          return (
            <g pointerEvents="none">
              <rect x={tipX} y={tipY} width="222" height="80" rx="8"
                    fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.2"
                    filter="drop-shadow(0 6px 14px rgba(15,23,42,0.16))" />
              <text x={tipX + 12} y={tipY + 18} fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="'JetBrains Mono',monospace">w₁={d.w1.toFixed(4)}  w₂={d.w2.toFixed(4)}</text>
              <text x={tipX + 12} y={tipY + 34} fontSize="11" fill="#475569" fontFamily="'JetBrains Mono',monospace">w(H₂O)={d.ww.toFixed(4)}  T={d.temp}°C</text>
              <text x={tipX + 12} y={tipY + 50} fontSize="11" fontWeight="700" fill={d.color} fontFamily="'IBM Plex Sans',sans-serif">{d.solidPhase}</text>
              <text x={tipX + 12} y={tipY + 66} fontSize="10" fill="#94a3b8" fontFamily="'JetBrains Mono',monospace">点击查看 →</text>
            </g>
          );
        })()}
        {hoverInfo && hoverInfo.kind === "surface" && (() => {
          const tipX = Math.min(W - 240, hoverInfo.sx + 14);
          const tipY = Math.max(10, hoverInfo.sy - 70);
          return (
            <g pointerEvents="none">
              <rect x={tipX} y={tipY} width="232" height="60" rx="8"
                    fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.2"
                    filter="drop-shadow(0 6px 14px rgba(15,23,42,0.16))" />
              <text x={tipX + 12} y={tipY + 18} fontSize="11" fontWeight="700" fill="#0f172a" fontFamily="'IBM Plex Sans',sans-serif">PC-CVAE 预测面</text>
              <text x={tipX + 12} y={tipY + 34} fontSize="11" fill="#475569" fontFamily="'JetBrains Mono',monospace">w₁={hoverInfo.w1.toFixed(4)}  T={hoverInfo.T.toFixed(0)}°C</text>
              <text x={tipX + 12} y={tipY + 50} fontSize="11" fontWeight="700" fill="#0284c7" fontFamily="'JetBrains Mono',monospace">w₂_sat={hoverInfo.w2sat.toFixed(4)}</text>
            </g>
          );
        })()}
      </svg>

      {/* HUD */}
      <div style={{
        position: "absolute", top: 12, left: 14,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10, color: "#64748b",
        background: "rgba(255,255,255,0.75)",
        backdropFilter: "blur(4px)",
        padding: "6px 10px",
        border: "1px solid #e2e8f0",
        borderRadius: 6,
        lineHeight: 1.55,
      }}>
        <div>θ {(theta * 180 / Math.PI).toFixed(0)}° · φ {(phi * 180 / Math.PI).toFixed(0)}°</div>
        <div>zoom {(scale / 360).toFixed(2)}×</div>
        <div style={{ color: "#94a3b8", marginTop: 3 }}>拖拽旋转 · 滚轮缩放</div>
      </div>

      {/* Reset button */}
      <button
        onClick={() => { setTheta(0.65); setPhi(0.42); setScale(360); }}
        style={{
          position: "absolute", top: 12, right: 14,
          padding: "5px 12px",
          fontSize: 11, fontWeight: 600,
          fontFamily: "'IBM Plex Sans', sans-serif",
          color: "#475569",
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(4px)",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          cursor: "pointer",
        }}
      >↺ 重置视角</button>
    </div>
  );
}

